import type { PersonnelCatalogEntry, PlantillaVacantItem } from '../personnelTypes';

/** What the service-record form needs filled to enable its primary action, and then some. */
export interface SampleServiceRecord {
  plantillaPositionId: string;
  plantillaItemId: string;
  effectiveFrom: string;
  appointmentDate: string;
  assumptionDate: string;
  appointmentNatureId: string | null;
  remarks: string;
}

/**
 * One line of service, out of the LIVE vacant list and the LIVE nature catalog.
 *
 * **It picks the SLOT and reads the position off it**, never both independently: two separate
 * picks produce an item that does not belong to the position above it, which the server refuses
 * and which reads as a bug in the form rather than in the generator.
 *
 * **It takes the catalog rather than inventing an id**, for the same reason: an id the generator
 * made up fails the server's "it must exist and be active" check. A generator with no parameters
 * cannot pick a real row, so it silently fills none of them — and a generated record that is a
 * column of em dashes exercises none of the screens behind the form.
 *
 * Null when the plantilla has nothing open — there is no honest value to invent, and the caller
 * disables the button rather than filling a field the server would reject.
 */
export function sampleServiceRecord(
  vacant: PlantillaVacantItem[],
  natures: PersonnelCatalogEntry[],
): SampleServiceRecord | null {
  if (vacant.length === 0) return null;

  const item = vacant[Math.floor(Math.random() * vacant.length)];

  // Today, as a calendar date: a line of service taking effect in the future is legitimate but
  // is a decision, and a generator does not get to make it.
  const today = new Date();
  const effectiveFrom = calendarDate(today);

  /*
    The instrument is dated BEFORE the assumption, which is the one ordering the server refuses
    to have backwards — a generator that produced a rejection would be worse than one filling
    less. Two weeks is the ordinary gap between a signed appointment and the day someone reports.
  */
  const appointed = new Date(today);
  appointed.setDate(appointed.getDate() - 14);

  return {
    plantillaPositionId: item.plantillaPositionId,
    plantillaItemId: item.id,
    effectiveFrom,
    appointmentDate: calendarDate(appointed),

    // Assumed on the day the line takes effect, which is the ordinary case.
    assumptionDate: effectiveFrom,

    // A real row, or nothing: the field is optional, so an empty catalog is not a failure.
    appointmentNatureId: natures.length === 0
      ? null
      : natures[Math.floor(Math.random() * natures.length)].id,

    remarks: 'Recorded for testing.',
  };
}

/**
 * A calendar date from a local Date — never `toISOString`, which converts to UTC first.
 *
 * For a reader east of UTC that shifts the day, and every date on this form is a day rather
 * than an instant.
 */
function calendarDate(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${value.getFullYear()}-${month}-${day}`;
}
